import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../api/axios';
import { jwtDecode } from 'jwt-decode';
import { requestNotificationPermission } from '../services/pushNotifications';
import { getViewMode, setViewMode as persistViewMode, getEffectiveRole } from '../utils/viewMode';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewModeState] = useState(getViewMode);

    useEffect(() => {
        // Check for token in localStorage on mount
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                // Check if token is expired
                if (decoded.exp * 1000 < Date.now()) {
                    localStorage.removeItem('token');
                    setUser(null);
                } else {
                    const userData = { ...decoded, token };
                    setUser(userData);
                    // Register for push notifications if authenticated
                    requestNotificationPermission(decoded.sub);
                }
            } catch (err) {
                localStorage.removeItem('token');
                setUser(null);
            }
        }
        setLoading(false);
    }, []);

    const login = async (email, password) => {
        setLoading(true);
        setError(null);
        try {
            // URLSearchParams for OAuth2 compatible endpoint (application/x-www-form-urlencoded)
            const params = new URLSearchParams();
            params.append('username', email); // OAuth2 expects 'username'
            params.append('password', password);

            const response = await api.post('/auth/login', params, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });

            const { access_token } = response.data;
            localStorage.setItem('token', access_token);
            
            const decoded = jwtDecode(access_token);
            setUser({ ...decoded, token: access_token });
            
            // Register for push notifications after login
            requestNotificationPermission(decoded.sub);
            
            return true;
        } catch (err) {
            console.error("Login failed", err);
            setError(err.response?.data?.detail || "Login failed");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const register = async (email, password, fullName, organizationName) => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.post('/auth/register', {
                email,
                password,
                full_name: fullName,
                organization_name: organizationName
            });

            const { access_token } = response.data;
            localStorage.setItem('token', access_token);
            
            const decoded = jwtDecode(access_token);
            setUser({ ...decoded, token: access_token });
            
            // Register for push notifications after registration
            requestNotificationPermission(decoded.sub);
            
            return true;
        } catch (err) {
            console.error("Registration failed", err);
            setError(err.response?.data?.detail || "Registration failed");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setUser(null);
        // Clear the httpOnly session cookie that gates server-rendered pages
        fetch('/auth/logout', { method: 'POST' }).finally(() => {
            window.location.href = '/login';
        });
    };

    const setViewMode = (mode) => {
        persistViewMode(mode);
        setViewModeState(mode === 'worker' ? 'worker' : 'manager');
    };

    const actualRole = user?.role || 'manager';
    const effectiveRole = getEffectiveRole(user, viewMode);

    return (
        <AuthContext.Provider value={{
            user, login, register, logout, loading, error,
            viewMode, setViewMode, effectiveRole, actualRole,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
